import dayjs, { Dayjs } from 'dayjs';
import { Calendar, CalendarEvent, CalendarEventAttendeeResponseStatus } from '@demind-inc/core';
import { DataTFunction } from '../../DataView/i18n';

export const hasSameDate = (date1?: Date, date2?: Dayjs) => {
  if (!date1 || !date2) {
    return true;
  }

  return dayjs(date1).isSame(date2);
};

export const canManageCalendarEventRecurrence = (
  calendarType?: string | null,
  isAppleDesktopSupported = false
) => {
  if (!calendarType) {
    return false;
  }

  return (
    calendarType === 'google' ||
    calendarType === 'outlook' ||
    calendarType === 'lifestack' ||
    (calendarType === 'apple' && isAppleDesktopSupported)
  );
};

export type SupportedEventResponseStatus = Extract<
  CalendarEventAttendeeResponseStatus,
  'accepted' | 'tentative' | 'declined'
>;

export const RESPONSE_STATUS_OPTIONS: Array<{
  value: SupportedEventResponseStatus;
}> = [{ value: 'accepted' }, { value: 'declined' }, { value: 'tentative' }];

export const getResponseStatusOptions = (t: DataTFunction) =>
  RESPONSE_STATUS_OPTIONS.map((option) => ({
    ...option,
    label: t(`calendar.invite.responses.${option.value}`),
  }));

export const isSupportedResponseStatus = (value: string): value is SupportedEventResponseStatus => {
  return RESPONSE_STATUS_OPTIONS.some((option) => option.value === value);
};

export const getEventRecurrenceString = (
  event?: { recurrence?: unknown } | null
): string | null => {
  if (!event) {
    return null;
  }

  const recurrence = event.recurrence;
  if (
    recurrence &&
    typeof recurrence === 'object' &&
    'string' in recurrence &&
    typeof (recurrence as { string?: unknown }).string === 'string'
  ) {
    return (recurrence as { string: string }).string;
  }

  return null;
};

export const getCurrentUserResponseStatus = (
  event?: Pick<Partial<CalendarEvent>, 'attendees'>,
  targetCalendar?: Pick<Calendar, 'internalGCalendarId' | 'rootEmail'>,
  userEmail?: string
): CalendarEventAttendeeResponseStatus | undefined => {
  if (!event?.attendees?.length) {
    return undefined;
  }

  const currentUserEmails = [
    targetCalendar?.internalGCalendarId,
    targetCalendar?.rootEmail,
    userEmail,
  ].filter((email): email is string => !!email);
  const meAttendee = event.attendees.find((attendee) => currentUserEmails.includes(attendee.email));

  if (!meAttendee) {
    return undefined;
  }

  return meAttendee.responseStatus;
};

export const getInviteResponseTitle = (
  t: DataTFunction,
  responseStatus?: CalendarEventAttendeeResponseStatus
) => {
  switch (responseStatus) {
    case 'accepted':
      return t('calendar.invite.titles.accepted');
    case 'tentative':
      return t('calendar.invite.titles.tentative');
    case 'declined':
      return t('calendar.invite.titles.declined');
    default:
      return t('calendar.invite.titles.awaiting');
  }
};

export const getInviteResponseChipLabel = (
  t: DataTFunction,
  responseStatus?: CalendarEventAttendeeResponseStatus
) => {
  switch (responseStatus) {
    case 'accepted':
      return t('calendar.invite.status.accepted');
    case 'tentative':
      return t('calendar.invite.status.tentative');
    case 'declined':
      return t('calendar.invite.status.declined');
    default:
      return t('calendar.invite.status.awaiting');
  }
};
